import { LoadingOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, message } from 'antd';
import useModal from 'hooks/useModal';
import { useEffect, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import ProductSelector, { Mode } from './index';
import Product from './Product';
import useProduct from './useProduct';

const Container = styled.div`
  > * {
    margin-bottom: 14px;
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('btn-link')} {
    padding: 0;
  }
`;

const AddButton = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  background: rgba(247, 249, 250, 1);
  border: 1px dashed rgba(211, 215, 224, 1);
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: var(--ant-primary-color);
    border-color: var(--ant-primary-color);
  }

  > span {
    margin-right: 4px;
  }
`;

const ProductWrapper = styled.div`
  padding: 10px 8px;
  border: solid 1px #d3d7e0;
  border-radius: 2px;
`;

const showProductCount = 3;

export interface AddProductProps {
  storeId: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}

const AddProduct = (props: AddProductProps) => {
  const { storeId, disabled = false, value = [], onChange } = props;

  const { t } = useTranslate();

  const { visible, show, hide, meta: mode } = useModal<Mode>();

  const { loading, list, fetchList, setList } = useProduct();

  const refsCurrent = {
    fetchList,
    setList,
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const showProductIdList = value.slice(0, showProductCount);

    const {
      current: { fetchList, setList, t },
    } = refs;

    if (showProductIdList.length === 0) {
      setList([]);

      return;
    }

    fetchList({
      internal_store_id: storeId,
      page_no: 1,
      page_size: showProductCount,
      external_item_ids: showProductIdList.join(','),
      itemStatus: 'ALL',
    }).catch(() => {
      message.error(t('voucher.获取商品列表失败，请稍后再试'));
    });
  }, [value, storeId]);

  return (
    <Container>
      {!disabled ? (
        <AddButton onClick={() => show('ALL')}>
          <PlusOutlined style={{ marginRight: 4 }} />
          {t('voucher.添加商品')}
        </AddButton>
      ) : null}
      {loading ? (
        <LoadingOutlined />
      ) : (
        (list || []).map((product) => {
          return (
            <ProductWrapper key={product.externalItemId}>
              <Product product={product} />
            </ProductWrapper>
          );
        })
      )}
      {value.length > showProductCount && (
        <Button type="link" onClick={() => show('SELECTED')}>
          {t('sider.voucher', { x: value.length - showProductCount })}
          <RightOutlined />
        </Button>
      )}
      <ProductSelector
        visible={visible}
        onCancel={() => hide()}
        mode={mode || 'ALL'}
        storeId={storeId}
        value={value}
        onChange={(values: string[]) => {
          onChange && onChange(values);
        }}
      />
    </Container>
  );
};

export default AddProduct;
